import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Search, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

export const DriversImportDialog = ({ open, stationId, onClose, onDone }) => {
  const [mode, setMode] = useState("file");
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (confirm) => {
    const fd = new FormData();
    if (mode === "file") fd.append("file", file); else fd.append("text", text);
    fd.append("station_id", stationId || "");
    fd.append("confirm", confirm);
    setBusy(true);
    try {
      const { data } = await api.post("/drivers/import", fd);
      if (confirm) { toast.success(`Aggiunti ${data.new} driver${data.reactivate ? `, riattivati ${data.reactivate}` : ""}`); onDone(); } else setPreview(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ready = mode === "file" ? !!file : text.trim().length > 1;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="drivers-import-dialog">
        <DialogHeader>
          <DialogTitle>Caricamento massivo driver</DialogTitle>
          <DialogDescription>Da Excel (colonna DRIVER o NOMINATIVO o NOME, facoltativa TELEFONO) oppure incollando un nome per riga. I nomi già presenti vengono saltati.</DialogDescription>
        </DialogHeader>
        <div className="inline-flex w-fit rounded-lg border border-slate-200 bg-white p-1">
          {[["file", "File Excel"], ["text", "Incolla elenco"]].map(([k, l]) => (
            <button key={k} onClick={() => { setMode(k); setPreview(null); }} data-testid={`drivers-import-mode-${k}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${mode === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>
        {mode === "file" ? (
          <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 text-sm text-slate-600 hover:border-slate-400">
            <FileSpreadsheet className="h-4 w-4 text-emerald-600" /><span className="truncate">{file ? file.name : "Scegli file Excel (.xlsx)..."}</span>
            <input type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="drivers-import-file-input" />
          </label>
        ) : (
          <Textarea rows={8} value={text} onChange={(e) => { setText(e.target.value); setPreview(null); }} placeholder={"Mario Rossi\nLuca Bianchi\n..."} data-testid="drivers-import-text" />
        )}
        <Button onClick={() => send(false)} disabled={!ready || busy} data-testid="drivers-import-preview-button"><Search className="mr-2 h-4 w-4" /> Analizza</Button>
        {preview && (
          <div className="space-y-3 rounded-lg border p-4 text-sm" data-testid="drivers-import-preview">
            <p><b data-testid="drivers-import-new">{preview.new}</b> nuovi driver · {preview.reactivate} da riattivare · {preview.already} già presenti</p>
            {preview.names.length > 0 && <p className="max-h-32 overflow-auto text-xs text-slate-600">{preview.names.join(" · ")}</p>}
            {preview.errors.length > 0 && (
              <div className="max-h-32 overflow-auto rounded-md bg-rose-50 p-2 text-xs text-rose-700" data-testid="drivers-import-errors">
                {preview.errors.map((e, i) => <p key={i}>Riga {e.row} ({e.name}): {e.error}</p>)}
              </div>
            )}
            <Button onClick={() => send(true)} disabled={busy || !(preview.new + preview.reactivate)} data-testid="drivers-import-confirm-button"><Upload className="mr-2 h-4 w-4" /> Carica driver</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
